import { useEffect, useState } from 'react';
import { Banknote, Camera, Plus, Minus, Lock, Fuel, ShoppingBag, History } from 'lucide-react';
import { StitekPlochy, RadekPlochy, TlacitkoPlochy, barvyPlochy, fmtKc, fmtCas } from './PlochaUI';

export const PLATBA = { karta: 'kartou', hotovost: 'hotově', prevod: 'převodem' };

/**
 * Plocha „Moje pokladna" — pod kartou cockpitu, když člověk klepne na dlaždici
 * Pokladna. Kolik mám u sebe, kolik objednávek a jaký průměr, a účtenky, které
 * jsem dnes nafotil.
 *
 * Jedno místo na focení účtenek: „Vyfoť účtenku". Tankování se pozná samo
 * (sken najde palivo → zapíše se k autu, které držím), takže řidič nemusí
 * vybírat mezi „Nákup" a „Natankovat" — dřív šla každá šestá tankovací
 * účtenka špatným tlačítkem. Historie tankování je proto tady, mezi účtenkami.
 *
 * Účtenky se neproplácejí zvlášť: převezme je manažer se schválením
 * vyrovnání, spolu s penězi (VyuctovaniService::prevezmiUctenky).
 *
 * Objednávky: u rozvozové pokladny moje rozvozy od příchodu, u hlavní celý
 * provoz (stejná čísla jako živá karta směny v Přehledu).
 */
export default function MojePokladnaPanel({
    pokladnaSmena, obnova = 0,
    onVyfotit, onVklad, onVydej, onHistorie, isDark,
}) {
    const [data, setData] = useState(null);
    const b = barvyPlochy(isDark);
    const zelena = isDark ? '#34D399' : '#059669';

    useEffect(() => {
        let zivy = true;
        const nacti = () => fetch('/api/me/moje-pokladna')
            .then(r => (r.ok ? r.json() : Promise.reject()))
            .then(d => { if (zivy) setData(d); })
            .catch(() => {});
        nacti();
        const iv = setInterval(nacti, 60000);
        return () => { zivy = false; clearInterval(iv); };
    }, [obnova]);

    if (!pokladnaSmena) return null;

    const jeHlavni = pokladnaSmena.typ === 'bistro_hlavni';
    // Po odeslání vyúčtování se pokladna jen dívá: vklad, výdej ani nákup už ne.
    const uzavrena = !!(pokladnaSmena.stav && pokladnaSmena.stav !== 'otevreno');
    const obdobiOd = pokladnaSmena.obdobi_od || pokladnaSmena.otevreno_at;
    const obj = data?.objednavky;
    const uctenky = data?.uctenky || [];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 12px 12px' }}>
            <StitekPlochy Ikona={Banknote} barva={zelena} isDark={isDark}
                vpravo={[pokladnaSmena.nazev, obdobiOd ? `od ${fmtCas(obdobiOd)}` : null].filter(Boolean).join(' · ')}>
                Moje pokladna · dnes
            </StitekPlochy>

            {/* Peníze na jednom místě: co mám u sebe a co se dnes utržilo.
                Dřív to byly dvě karty a pod nimi dvě dlaždice s počtem
                a průměrem — plocha se kvůli tomu musela rolovat, a přitom
                jde o čtyři čísla. */}
            <div style={{
                display: 'flex', flexDirection: 'column', gap: 9,
                padding: '11px 13px 12px', borderRadius: 12,
                background: b.deska, border: `1px solid ${b.okraj}`, boxShadow: b.hloubka,
            }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 11.5, fontWeight: 600, color: b.tlumena }}>
                            {jeHlavni ? 'V pokladně' : 'Mám u sebe'}
                        </span>
                        <span style={{
                            fontSize: 27, fontWeight: 800, letterSpacing: -0.6, lineHeight: 1.05,
                            color: b.primarni, fontVariantNumeric: 'tabular-nums',
                        }}>{fmtKc(pokladnaSmena.aktualni_kc)}</span>
                        <span style={{ fontSize: 11, color: b.slaba, fontVariantNumeric: 'tabular-nums' }}>
                            hotově {fmtKc(pokladnaSmena.trzba_kc)} · výdej {fmtKc(pokladnaSmena.vydaj_kc)}
                            {Number(pokladnaSmena.vklad_kc) > 0 ? ` · vklad ${fmtKc(pokladnaSmena.vklad_kc)}` : ''}
                        </span>
                    </span>
                    {obj && (
                        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, minWidth: 0 }}>
                            <span style={{ fontSize: 11.5, fontWeight: 600, color: b.tlumena }}>Tržba</span>
                            <span style={{
                                fontSize: 27, fontWeight: 800, letterSpacing: -0.6, lineHeight: 1.05,
                                color: b.primarni, fontVariantNumeric: 'tabular-nums',
                            }}>{fmtKc(obj.trzba)}</span>
                            {/* KPI jednou větou místo dvou dlaždic s popisky. */}
                            <span style={{ fontSize: 11, color: b.slaba, fontVariantNumeric: 'tabular-nums' }}>
                                {obj.pocet} {obj.rozsah === 'moje' ? 'rozvozů' : 'objednávek'}
                                {obj.prumer != null ? ` · ⌀ ${fmtKc(obj.prumer)}` : ''}
                            </span>
                        </span>
                    )}
                </div>
                {obj?.platby && <RozpadTrzby platby={obj.platby} isDark={isDark} />}
                {/* Hlavní pokladna: tržba je něco jiného než hotovost v šuplíku.
                    Svoje objednávky (pult, konzumace, výdej, co nemá rozvozce)
                    a k nim tržba rozvozců, které manažer při vyrovnání převzal.
                    Co rozvozci ještě drží, se sem přičte až jejich vyrovnáním. */}
                {obj?.od_rozvozcu && (
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', gap: 8,
                        fontSize: 11, color: b.slaba, fontVariantNumeric: 'tabular-nums',
                        borderTop: `1px solid ${b.okraj}`, paddingTop: 7,
                    }}>
                        <span>moje {fmtKc(obj.vlastni?.celkem)} · převzato {fmtKc(obj.od_rozvozcu.prevzato.celkem)}</span>
                        {obj.od_rozvozcu.ceka.pocet_ridicu > 0 && (
                            <span style={{ color: '#F59E0B', fontWeight: 600 }}>
                                u rozvozců {fmtKc(obj.od_rozvozcu.ceka.celkem)}
                            </span>
                        )}
                    </div>
                )}
            </div>

            {uzavrena && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 10,
                    fontSize: 12.5, color: '#0CC0DF',
                    background: 'rgba(12,192,223,0.10)', border: '1px solid rgba(12,192,223,0.30)',
                }}>
                    <Lock size={14} /> Pokladna je uzavřená — čeká na schválení manažerem.
                </div>
            )}

            <div style={{ display: 'flex' }}>
                <TlacitkoPlochy isDark={isDark} hlavni Ikona={Camera} onClick={onVyfotit} disabled={uzavrena}>
                    Vyfoť účtenku
                </TlacitkoPlochy>
            </div>
            {/* Ruční vklad a výdej jen na hlavní pokladně (majitel, banka,
                záloha ze mzdy). Na rozvozové pokladně hýbou hotovostí jen
                objednávky, účtenky a vyúčtování — tlačítka by tam nabízela
                něco, co se nemá dělat. */}
            {jeHlavni && (
                <div style={{ display: 'flex', gap: 8 }}>
                    <TlacitkoPlochy isDark={isDark} Ikona={Plus} onClick={onVklad} disabled={uzavrena}>Vklad</TlacitkoPlochy>
                    <TlacitkoPlochy isDark={isDark} Ikona={Minus} onClick={onVydej} disabled={uzavrena}>Výdej</TlacitkoPlochy>
                </div>
            )}

            <StitekPlochy barva={b.tlumena} isDark={isDark}
                vpravo={onHistorie ? <button type="button" onClick={onHistorie} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5, padding: 0,
                    border: 'none', background: 'none', cursor: 'pointer',
                    fontFamily: 'inherit', fontSize: 11.5, fontWeight: 650, color: zelena,
                }}><History size={13} /> Starší</button> : null}>
                Dnešní účtenky
            </StitekPlochy>
            {uctenky.length ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {uctenky.map(u => <RadekUctenky key={u.id} u={u} isDark={isDark} />)}
                </div>
            ) : (
                <div style={{ fontSize: 13, color: b.tlumena, padding: '2px 2px 0' }}>
                    Dnes zatím žádná účtenka.
                </div>
            )}
        </div>
    );
}

/**
 * Tržba podle placení — kolik šlo hotově, kartou a online. Hotovost vidí
 * člověk v zůstatku, ale kartu a online ne, a právě na ně se ptá („kolik
 * mám na terminálu?"). Pruh ukazuje poměr, čísla pod ním sčítají tržbu.
 * Body (věrnostní) a nezařazené platby jen když nějaké jsou — jinak by
 * byly nula navíc. Třídí server stejně jako uzávěrka, ať čísla sedí.
 */
function RozpadTrzby({ platby, isDark }) {
    const b = barvyPlochy(isDark);
    const polozky = [
        { k: 'hotovost', label: 'Hotově', barva: '#F59E0B', vzdy: true },
        { k: 'karta', label: 'Kartou', barva: '#3B82F6', vzdy: true },
        { k: 'online', label: 'Online', barva: '#0CC0DF', vzdy: true },
        { k: 'body', label: 'Body', barva: '#A78BFA' },
        { k: 'jine', label: 'Jinak', barva: b.slaba },
    ].filter((x) => x.vzdy || Number(platby[x.k] || 0) > 0);
    const nenulove = polozky.filter((x) => Number(platby[x.k] || 0) > 0);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {nenulove.length > 0 && (
                <div aria-hidden="true" style={{ display: 'flex', gap: 2, height: 6, borderRadius: 3, overflow: 'hidden' }}>
                    {nenulove.map((x) => (
                        <span key={x.k} style={{ flex: Number(platby[x.k]), background: x.barva }} />
                    ))}
                </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
                {polozky.map((x) => (
                    <span key={x.k} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: b.tlumena }}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: x.barva, flexShrink: 0 }} />
                            {x.label}
                        </span>
                        <span style={{
                            fontSize: 14.5, fontWeight: 800, color: b.primarni, fontVariantNumeric: 'tabular-nums',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>{fmtKc(platby[x.k])}</span>
                    </span>
                ))}
            </div>
        </div>
    );
}

/** Řádek účtenky — tankování s litry a autem, nákup s dodavatelem. */
export function RadekUctenky({ u, isDark, sDatem = false }) {
    const tank = u.typ === 'tankovani';
    const Ikona = tank ? Fuel : ShoppingBag;
    const barva = tank ? '#3B82F6' : '#0CC0DF';
    const cas = sDatem && u.cas
        ? `${new Date(u.cas).getDate()}. ${new Date(u.cas).getMonth() + 1}. ${fmtCas(u.cas)}`
        : fmtCas(u.cas);
    return (
        <RadekPlochy isDark={isDark}
            znacka={
                <span style={{
                    width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    background: `${barva}22`, color: barva,
                }}><Ikona size={14} /></span>
            }
            hlavni={tank
                ? `Tankování${u.tankovani?.litry ? ` · ${String(u.tankovani.litry).replace('.', ',')} l` : ''}`
                : (u.dodavatel || 'Nákup')}
            vedlejsi={[
                tank ? u.tankovani?.spz : null,
                tank && u.tankovani?.stav === 'ceka_sken' && !u.cte_se ? 'doplň litry' : null,
            ].filter(Boolean).join(' · ') || null}
            vpravo={u.cte_se ? 'čte se…' : fmtKc(u.castka)}
            vpravoPod={[cas, PLATBA[u.platba]].filter(Boolean).join(' · ')}
        />
    );
}
